import { useEffect, useState } from "react";
import "./style.css";
import axios from "axios";
import { Helmet } from "react-helmet-async";
import Spinner from "../../global-components/src/components/spinner/spiner";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useAuth, useMessage } from "../../global-components/src";
import { Trash } from "react-feather";
import { errorMessage } from "../../global-components/src/utils/text";
import { BUCKET_PACOCA_URL, PACOCA_API_URL } from "../../global-components/src/utils/vars";
import Cookies from "js-cookie";
import { User } from "../../global-components/src/types";
import { useTranslation } from "react-i18next";
import LeftPanel from "./left-panel";
import { formatLoginPhoneInput, isPhoneLoginInput } from "../../utils/phone-login";

const ALLOWED_CLIENTS = ["versona", "reflexo", "pacoca"];
type TwoFactorMethod = "authenticator" | "pacoca_chat";

const Login: React.FC = () => {
    const navigate = useNavigate();
    const { t } = useTranslation();
    const [login, setLogin] = useState("");
    const [password, setPassword] = useState("");
    const [loading, setLoading] = useState(false);
    const [errorsInput, setErrorsInput] = useState<{ login?: string[]; password?: string[]; code?: string[] }>({});
    const [isVisible, setIsVisible] = useState(false);
    const [remember, setRemember] = useState(true);
    const { loginContext, changeUser, removeUserFromSession, isAuthenticated } = useAuth();
    const [usersSessions, setUsersSessions] = useState<User[]>([]);
    const location = useLocation();
    const { setMessage } = useMessage();
    const [requires2FA, setRequires2FA] = useState(false);
    const [twoFactorCode, setTwoFactorCode] = useState("");
    const [userId2FA, setUserId2FA] = useState<string | null>(null);
    const [twoFactorMethods, setTwoFactorMethods] = useState<TwoFactorMethod[]>([]);
    const [twoFactorMethod, setTwoFactorMethod] = useState<TwoFactorMethod | null>(null);


    // ── SSO params ──────────────────────────────────────────
    const getQueryParam = (param: string) => new URLSearchParams(location.search).get(param);

    const redirectUri = getQueryParam("redirect_uri");
    const clientId    = getQueryParam("client_id");
    const isSSOFlow   = !!redirectUri && !!clientId && ALLOWED_CLIENTS.includes(clientId);

    // ── forceNewLogin: em SSO sempre mostra contas salvas primeiro ──
    const [forceNewLogin, setForceNewLogin] = useState(
        !isSSOFlow && getQueryParam("forceNewLogin") === "true"
    );

    
    useEffect(() => {
        if(isAuthenticated && !forceNewLogin){
            navigate("/")
            return
        }
    }, [isAuthenticated, forceNewLogin])

    // ── SSO params preservados para o fluxo 2FA ──
    const [ssoClientId, setSsoClientId]       = useState<string | null>(null);
    const [ssoRedirectUri, setSsoRedirectUri] = useState<string | null>(null);

    // ────────────────────────────────────────────────────────

    useEffect(() => {
        if (!twoFactorMethod) return;
        const expectedLength = twoFactorMethod === "pacoca_chat" ? 8 : 6;
        if (twoFactorCode.length === expectedLength) handleVerify2FA();
    }, [twoFactorCode, twoFactorMethod]);

    const removeUserSession = (id_user: number) => {
        removeUserFromSession(id_user);
        const users = JSON.parse(Cookies.get("saved_users") ?? "[]");
        setUsersSessions(users);
    };

    useEffect(() => {
        setForceNewLogin(!isSSOFlow && getQueryParam("forceNewLogin") === "true");
        const loginParam = getQueryParam("login");
        if (loginParam) setLogin(loginParam);
    }, [location.search]);

    useEffect(() => {
        const users = JSON.parse(Cookies.get("saved_users") ?? "[]");
        setUsersSessions(users);
    }, []);

    useEffect(() => {
        // Em SSO, se houver contas salvas, mostra elas (não força novo login)
        if (isSSOFlow) {
            setForceNewLogin(usersSessions.length <= 0);
        } else {
            setForceNewLogin(usersSessions.length <= 0 || getQueryParam("forceNewLogin") === "true");
        }
    }, [usersSessions]);

    const togglePasswordVisibility = () => setIsVisible(!isVisible);

    // ── Após login bem-sucedido: redireciona ou vai para "/" ──
    const handlePostLogin = (data: { access_token: string; refresh_token?: string; user: User; auth_code?: string; redirect_uri?: string }) => {
        loginContext(data.access_token, data.user, remember, data.refresh_token);

        if (isSSOFlow && data.auth_code && data.redirect_uri) {
            window.location.href = `${data.redirect_uri}?code=${data.auth_code}`;
        } else {
            navigate("/");
        }
    };

    // ── Conta salva clicada ──────────────────────────────────
    const handleSessionClick = async (userId: number) => {
        if (!isSSOFlow) {
            changeUser(userId);
            return;
        }

        try {
            setLoading(true);

            const savedUsers: User[] = JSON.parse(Cookies.get("saved_users") ?? "[]");
            const savedUser = savedUsers.find((u) => u.id === userId);

            if (!savedUser?.token) {
                setMessage(t("generics.errors.sessionExpired"), "error");
                setForceNewLogin(true);
                return;
            }

            const response = await axios.post(
                `${PACOCA_API_URL}/sso/issue-code`,
                { redirect_uri: redirectUri, client_id: clientId },
                { headers: { Authorization: `Bearer ${savedUser.token}` } }
            );

            window.location.href = `${redirectUri}?code=${response.data.auth_code}`;
        } catch {
            // Token expirado no back — força novo login
            setMessage(t("generics.errors.sessionExpired"), "error");
            setForceNewLogin(true);
        } finally {
            setLoading(false);
        }
    };

    // ── Login normal ─────────────────────────────────────────
    const handleLogin = async (e: React.FormEvent<HTMLFormElement>) => {
        setLoading(true);
        setErrorsInput({});
        e.preventDefault();

        try {
            let errors: Record<string, string[]> = {};
            if (!login)    errors.login    = [t("errors.input.required", { field: "login" })];
            if (!password) errors.password = [t("errors.input.required", { field: t("login.password") })];
            if (Object.keys(errors).length > 0) throw { response: { data: { errors } } };

            const response = await axios.post(`${PACOCA_API_URL}/login`, {
                login,
                password,
                ...(isSSOFlow && { redirect_uri: redirectUri, client_id: clientId }),
            });

            if (response.data.requires_2fa) {
                const availableMethods = (Array.isArray(response.data.two_factor_methods)
                    ? response.data.two_factor_methods
                    : ["authenticator"])
                    .filter((method: unknown): method is TwoFactorMethod =>
                        method === "authenticator" || method === "pacoca_chat",
                    );
                const challenge = response.data.two_factor_challenge as string;
                const selectedMethod = availableMethods.length === 1 ? availableMethods[0] : null;

                setRequires2FA(true);
                setUserId2FA(challenge);
                setTwoFactorMethods(availableMethods);
                setTwoFactorMethod(selectedMethod);
                setTwoFactorCode("");
                // Preserva params SSO para usar no verify2FA
                setSsoClientId(response.data.sso_client_id ?? clientId);
                setSsoRedirectUri(response.data.sso_redirect_uri ?? redirectUri);


                setLoading(false);
                if (selectedMethod === "pacoca_chat") {
                    void requestPacocaChatCode(challenge);
                }
                return;
            }

            handlePostLogin(response.data);
        } catch (err: any) {
            if (err?.response?.data?.errors) {
                setErrorsInput(err.response.data.errors);
            } else {
                const type = err?.response?.data?.type_message || "error";
                setMessage(errorMessage(err), type);
            }
            setLoading(false);
        }
    };

    const requestPacocaChatCode = async (challenge = userId2FA, notify = true) => {
        if (!challenge) return;

        try {
            await axios.post(`${PACOCA_API_URL}/2fa/pacoca-chat/send`, {
                two_factor_challenge: challenge,
            });
            if (notify) {
                setMessage("Enviamos um código para a conversa Paçoca Segurança no Paçoca Papo.", "success");
            }
        } catch (err: any) {
            if (err?.response?.data?.errors) {
                setErrorsInput(err.response.data.errors);
            } else {
                setMessage(errorMessage(err), "error");
            }
        }
    };

    const selectTwoFactorMethod = async (method: TwoFactorMethod) => {
        setErrorsInput({});
        setTwoFactorCode("");
        setTwoFactorMethod(method);
        if (method === "pacoca_chat") {
            await requestPacocaChatCode();
        }
    };

    // ── Verificação 2FA ──────────────────────────────────────
    const handleVerify2FA = async () => {
        try {
            if (!twoFactorMethod) return;
            const expectedLength = twoFactorMethod === "pacoca_chat" ? 8 : 6;
            let errors: Record<string, string[]> = {};
            if (!twoFactorCode) errors.code = [t("login.twoFactor.codeRequired")];
            if (twoFactorCode.length !== expectedLength) {
                errors.code = [twoFactorMethod === "pacoca_chat"
                    ? "Digite o código de 8 dígitos do Paçoca Papo."
                    : t("login.twoFactor.codeLengthError")];
            }
            if (Object.keys(errors).length > 0) throw { response: { data: { errors } } };

            setLoading(true);

            const response = await axios.post(`${PACOCA_API_URL}/2fa/verify`, {
                two_factor_challenge: userId2FA,
                code: twoFactorCode,
                method: twoFactorMethod,
                // Repassa params SSO preservados do login
                ...(ssoClientId    && { client_id:    ssoClientId }),
                ...(ssoRedirectUri && { redirect_uri: ssoRedirectUri }),
            });

            handlePostLogin(response.data);
        } catch (err: any) {
            if (err?.response?.data?.errors) {
                setErrorsInput(err.response.data.errors);
            } else {
                const type = err?.response?.data?.type_message || "error";
                setMessage(errorMessage(err), type);
            }
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        const root = document.getElementById("root");
        if (root) root.style.overflowX = "hidden";
        return () => { if (root) root.style.overflow = ""; };
    }, []);

    useEffect(() => {
        if (window.ReactNativeWebView && typeof window.ReactNativeWebView.postMessage === "function") {
            window.ReactNativeWebView.postMessage(JSON.stringify({ statusBarStyle: "transparent" }));
        }
    }, []);

    return (
        <div className="auth-root">
            <Helmet>
                <title>Paçoca - Login</title>
                <meta name="description" content="Paçoca - Faça login no paçoca!" />
                <meta name="keywords" content="Paçoca, rede social brasileira, login, entrar, conta" />
                <meta name="author" content="Paçoca Inc." />
                <meta property="og:title" content="Paçoca - Faça login no paçoca" />
                <meta property="og:description" content="Paçoca - Faça login no paçoca" />
                <meta property="og:image" content="/img/link-preview.png?v=1" />
                <meta property="og:url" content={window.location.href} />
                <meta property="og:type" content="website" />
                <meta name="twitter:card" content="summary_large_image" />
                <meta name="twitter:title" content="Paçoca - Faça login no paçoca" />
                <meta name="twitter:description" content="Paçoca - Faça login no paçoca" />
                <meta name="twitter:image" content="/img/link-preview.png?v=1" />
            </Helmet>

            <div className="auth-layout">
                {/* ── Left panel ── */}
                <LeftPanel />

                {/* ── Right panel ── */}
                <div className="auth-panel-right">
                    <div className="auth-logo">
                        <img src="/img/pacoca-sem-braco.png" alt="Paçoca" className="auth-logo__img" />
                    </div>

                    {/* Banner indicando de qual app veio */}
                    {isSSOFlow && (
                        <div className="auth-sso-banner">
                            <span>Entrando via <strong>{clientId}</strong></span>
                        </div>
                    )}

                    <form
                        className="auth-form"
                        id="form-login"
                        onSubmit={(e) => {
                            e.preventDefault();
                            requires2FA && twoFactorMethod ? handleVerify2FA() : handleLogin(e);
                        }}
                    >
                        <h1 className="auth-title">
                            {requires2FA ? t("login.twoFactor.title") : t("login.welcome")}
                        </h1>
                        <p className="auth-subtitle">
                            {requires2FA
                                ? twoFactorMethod
                                    ? t("login.twoFactor.subtitle")
                                    : "Escolha como deseja confirmar o acesso."
                                : t("login.subtitle")}
                        </p>

                        {/* ── Contas salvas ── */}
                        {usersSessions.length >= 1 && !forceNewLogin ? (
                            <div className="auth-sessions">
                                {usersSessions.map((userSession) =>
                                    userSession.id ? (
                                        <div className="auth-session-card" key={userSession.user_id}>
                                            <div
                                                className="auth-session-card__inner"
                                                onClick={() => handleSessionClick(userSession?.id)}
                                            >
                                                {loading ? (
                                                    <Spinner />
                                                ) : (
                                                    <>
                                                        <img
                                                            className="auth-session-card__avatar"
                                                            src={
                                                                userSession.img_account
                                                                    ? BUCKET_PACOCA_URL + "/" + userSession.img_account.replace(/^(\.\.\/)+/, "")
                                                                    : "/img/img-account.png"
                                                            }
                                                            alt={userSession.name}
                                                            onError={(e) => { e.currentTarget.src = "/img/img-account.png"; }}
                                                        />
                                                        <div className="auth-session-card__info">
                                                            <span className="auth-session-card__name">
                                                                {userSession?.name?.split(" ")[0]}
                                                            </span>
                                                            <span className="auth-session-card__handle">
                                                                {"@" + userSession.user_name}
                                                            </span>
                                                        </div>
                                                    </>
                                                )}
                                            </div>
                                            <button
                                                type="button"
                                                className="auth-session-card__remove"
                                                onClick={() => removeUserSession(userSession?.id)}
                                                aria-label="Remover conta salva"
                                            >
                                                <Trash size={16} />
                                            </button>
                                        </div>
                                    ) : null
                                )}
                                <div className="auth-center-link" style={{ marginTop: 20 }}>
                                    <span className="auth-link" onClick={() => setForceNewLogin(true)}>
                                        {t("login.otherAccount")}
                                    </span>
                                </div>
                            </div>
                        ) : (
                            <>
                                {/* ── 2FA ── */}
                                {requires2FA ? (
                                    twoFactorMethod ? (
                                        <div className="auth-field">
                                            <button
                                                type="button"
                                                className="auth-link auth-link--small"
                                                style={{ border: 0, background: "transparent", padding: 0, marginBottom: 16 }}
                                                onClick={() => { setTwoFactorMethod(null); setTwoFactorCode(""); setErrorsInput({}); }}
                                            >
                                                Escolher outro método
                                            </button>
                                            <label className="auth-field__label">
                                                {twoFactorMethod === "pacoca_chat" ? "Código do Paçoca Papo" : t("login.twoFactor.codeLabel")}
                                            </label>
                                            {twoFactorMethod === "pacoca_chat" && (
                                                <p className="auth-field__hint" style={{ marginTop: -4 }}>
                                                    Abra a conversa Paçoca Segurança no Paçoca Papo para consultar o código.
                                                </p>
                                            )}
                                            <div className="auth-field__wrap">
                                                <input
                                                    type="text"
                                                    inputMode="numeric"
                                                    autoComplete="one-time-code"
                                                    placeholder={twoFactorMethod === "pacoca_chat" ? "00000000" : "000 000"}
                                                    value={twoFactorCode}
                                                    onChange={event => setTwoFactorCode(event.target.value.replace(/\D/g, ""))}
                                                    className={`auth-input auth-input--center ${errorsInput.code ? "auth-input--error" : ""}`}
                                                    maxLength={twoFactorMethod === "pacoca_chat" ? 8 : 6}
                                                />
                                            </div>
                                            {twoFactorMethod === "pacoca_chat" && (
                                                <button type="button" className="auth-link auth-link--small" style={{ border: 0, background: "transparent", padding: 0, marginTop: 10 }} onClick={() => requestPacocaChatCode()}>
                                                    Enviar outro código
                                                </button>
                                            )}
                                            {errorsInput.code && <span className="auth-error">{errorsInput.code[0]}</span>}
                                        </div>
                                    ) : (
                                        <div className="auth-field">
                                            <label className="auth-field__label">Como você quer confirmar o acesso?</label>
                                            <p className="auth-field__hint">Escolha um método para receber ou gerar o código.</p>
                                            <div style={{ display: "grid", gap: 10 }}>
                                                {twoFactorMethods.map(method => (
                                                    <button
                                                        key={method}
                                                        type="button"
                                                        className="auth-btn-primary"
                                                        style={{ textAlign: "left", padding: "14px 16px" }}
                                                        onClick={() => selectTwoFactorMethod(method)}
                                                    >
                                                        <strong>{method === "pacoca_chat" ? "Paçoca Papo" : "Aplicativo autenticador"}</strong>
                                                        <span style={{ display: "block", fontSize: 13, opacity: 0.8, marginTop: 4 }}>
                                                            {method === "pacoca_chat" ? "Receber código na conversa Paçoca Segurança" : "Usar código gerado pelo seu autenticador"}
                                                        </span>
                                                    </button>
                                                ))}
                                            </div>
                                        </div>
                                    )
                                ) : (
                                    <>
                                        {/* Login */}
                                        <div className="auth-field">
                                            <label className="auth-field__label" htmlFor="input-username">
                                                {t("login.inputLogin")}
                                            </label>
                                            <div className="auth-field__wrap">
                                                <span className="auth-field__icon">
                                                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/></svg>
                                                </span>
                                                <input
                                                    autoCapitalize="none"
                                                    placeholder={t("login.inputLogin")}
                                                    id="input-username"
                                                    type="text"
                                                    inputMode={isPhoneLoginInput(login) ? "tel" : "text"}
                                                    value={login}
                                                    onChange={(e) => setLogin(formatLoginPhoneInput(e.target.value))}
                                                    className={`auth-input ${errorsInput.login ? "auth-input--error" : ""}`}
                                                    name="login"
                                                    autoComplete="username"
                                                />
                                            </div>
                                            {errorsInput.login && (
                                                <span className="auth-error">{errorsInput.login[0]}</span>
                                            )}
                                        </div>

                                        {/* Senha */}
                                        <div className="auth-field">
                                            <div className="auth-field__row">
                                                <label className="auth-field__label" htmlFor="input-password">
                                                    {t("login.inputPassword")}
                                                </label>
                                            </div>
                                            <div className="auth-field__wrap">
                                                <span className="auth-field__icon">
                                                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="3" y="11" width="18" height="11" rx="2" ry="2"/><path d="M7 11V7a5 5 0 0 1 10 0v4"/></svg>
                                                </span>
                                                <input
                                                    type={isVisible ? "text" : "password"}
                                                    placeholder={t("login.inputPassword")}
                                                    id="input-password"
                                                    value={password}
                                                    onChange={(e) => setPassword(e.target.value)}
                                                    className={`auth-input ${errorsInput.password ? "auth-input--error" : ""}`}
                                                    name="password"
                                                    autoComplete="current-password"
                                                />
                                                <img
                                                    className="view-password user-select-none"
                                                    id="view-password"
                                                    src={isVisible ? "/img/eye-off.svg" : "/img/eye.svg"}
                                                    alt="Mostrar senha"
                                                    onClick={togglePasswordVisibility}
                                                    style={{ height: "18px", cursor: "pointer", position: "absolute", right: "15px", top: "50%", transform: "translateY(-50%)" }}
                                                />
                                            </div>
                                            <div className="d-flex justify-content-end mt-1">
                                                <Link className="auth-link auth-link--small" to={`/esqueci-minha-senha?login=${login}`}>
                                                    {t("login.forgotPassword")}
                                                </Link>
                                            </div>

                                            {errorsInput.password && (
                                                <span className="auth-error">{errorsInput.password[0]}</span>
                                            )}
                                        </div>

                                        {/* Lembrar */}
                                        <label className="auth-checkbox">
                                            <input
                                                type="checkbox"
                                                checked={remember}
                                                onChange={() => setRemember(!remember)}
                                                className="auth-checkbox__input"
                                            />
                                            <span className="auth-checkbox__box" />
                                            <span className="auth-checkbox__label">{t("login.keepLoggedIn")}</span>
                                        </label>
                                    </>
                                )}

                                {/* Submit */}
                                {(!requires2FA || twoFactorMethod) && (
                                <button type="submit" className="auth-btn-primary" disabled={loading}>
                                    {loading ? <Spinner /> : (
                                        <span>{requires2FA ? "Verificar código" : t("login.enter")}</span>
                                    )}
                                </button>
                                )}

                                {usersSessions.length >= 1 && (
                                    <div className="auth-center-link" style={{ marginTop: 12 }}>
                                        <span className="auth-link" onClick={() => setForceNewLogin(false)}>
                                            {t("login.savedAccounts")}
                                        </span>
                                    </div>
                                )}

                                <div className="auth-center-link" style={{ marginTop: 16 }}>
                                    <Link className="auth-link" to="/cadastro">
                                        {t("login.dontHaveAccount")} <strong>{t("login.register")}</strong>
                                    </Link>
                                </div>
                            </>
                        )}
                    </form>
                </div>
            </div>
        </div>
    );
};

export default Login;